/**
 * Typed error contract for Re-Engage API responses.
 *
 * Backend (backend/src/routes/dialerReEngage.mjs) returns:
 *   { error: { code, message, retryAfter? } }
 *
 * Frontend reads `err.code` (and `err.retryAfter` for RATE_LIMITED) instead of
 * pattern-matching `err.message`. Survives error wrappers that strip ad-hoc
 * properties because we use a real class with `instanceof`.
 *
 * 503-no-envelope is treated as UNKNOWN_503 (NOT RE_ENGAGE_DISABLED) — the
 * disabled label is set ONLY by an explicit `code === "RE_ENGAGE_DISABLED"`
 * from the typed envelope. A bare 503 could be a cold start, LB blip, or
 * malformed body; mistyping it as "feature disabled" would silently mask
 * outages as a configuration problem.
 *
 * 401 → AUTH_EXPIRED: post-matcher-fix (Commit 1), session expiry on
 * /api/proxy/* returns a real 401 instead of being short-circuited by
 * middleware redirect to /login HTML. The page handles AUTH_EXPIRED via
 * a synchronous union predicate (see shouldRenderAuthExpired) and the
 * global QueryCache.onError handler invalidates the feature-flag query.
 *
 * Infra error codes (UNKNOWN_502/UNKNOWN_503/UNKNOWN_504) are explicit
 * branches because each indicates a different upstream failure mode worth
 * distinguishing in operator logs and UX copy. They are NOT treated as
 * session-expiry — a 5xx HTML page from Cloud Run or Vercel is an upstream
 * outage, not auth, and rendering "Session expired" would mislead the user.
 */

export class ReEngageApiError extends Error {
  readonly code: string;
  readonly retryAfter?: number;
  readonly httpStatus: number;
  /**
   * Extra fields the typed error envelope may carry beyond
   * {code, message, retryAfter}. Used by 422 PARALLEL_LINES_EXCEEDS_PLAN
   * (carries `limit` + `planTier`) and similar code-specific shapes.
   * Consumers narrow via `code` before reading.
   */
  readonly details?: Record<string, unknown>;

  constructor(args: {
    code: string;
    message: string;
    retryAfter?: number;
    httpStatus: number;
    details?: Record<string, unknown>;
  }) {
    super(args.message);
    this.name = "ReEngageApiError";
    this.code = args.code;
    this.retryAfter = args.retryAfter;
    this.httpStatus = args.httpStatus;
    this.details = args.details;
  }
}

export async function parseReEngageError(
  res: Response,
): Promise<ReEngageApiError | null> {
  if (res.ok) return null;
  const body = await res.json().catch(() => null);
  if (body?.error?.code) {
    // Strip the well-known fields; whatever remains is `details` (e.g.
    // `limit` + `planTier` on PARALLEL_LINES_EXCEEDS_PLAN, `cohortSize` on
    // COHORT_TOO_LARGE). Lets the wizard read typed envelope fields without
    // re-parsing the response.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { code: _c, message: _m, retryAfter: _r, ...rest } = body.error;
    return new ReEngageApiError({
      code: body.error.code,
      message: body.error.message ?? "Request failed",
      retryAfter: body.error.retryAfter,
      httpStatus: res.status,
      details: Object.keys(rest).length > 0 ? rest : undefined,
    });
  }
  if (res.status === 401) {
    return new ReEngageApiError({
      code: "AUTH_EXPIRED",
      message: "Session expired",
      httpStatus: 401,
    });
  }
  if (res.status === 502) {
    return new ReEngageApiError({
      code: "UNKNOWN_502",
      message: "Re-Engage service temporarily unavailable (bad gateway)",
      httpStatus: 502,
    });
  }
  if (res.status === 503) {
    return new ReEngageApiError({
      code: "UNKNOWN_503",
      message: "Re-Engage service temporarily unavailable",
      httpStatus: 503,
    });
  }
  if (res.status === 504) {
    return new ReEngageApiError({
      code: "UNKNOWN_504",
      message: "Re-Engage service temporarily unavailable (gateway timeout)",
      httpStatus: 504,
    });
  }
  // Note: we previously treated any non-JSON non-OK response as AUTH_EXPIRED
  // for defense-in-depth against middleware-redirected Re-Engage URLs.
  // Removed because (a) Commit 1's matcher exclusion makes that case
  // impossible for /api/proxy/*, and (b) the broader catch was misclassifying
  // genuine infra errors (Cloud Run 5xx HTML pages, Vercel edge errors) as
  // session expiry — leading the user to "Sign in again" only to discover
  // they're already signed in. Infra errors now surface as UNKNOWN with the
  // real status code, which is the correct UX signal.
  return new ReEngageApiError({
    code: "UNKNOWN",
    message: `Request failed (${res.status})`,
    httpStatus: res.status,
  });
}
